// import { i18n } from "@/i18n.config";
import { fallbackLng, languages } from '@/app/i18n/settings';
import {
    CUSTOMER_LEVEL,
    LinkTarget,
    MSProductVariantPreset,
} from '@prisma/client';
import { ObjectID } from 'bson';
import CryptoJS from 'crypto-js';
import { format } from 'date-fns';
import { HTMLAttributeAnchorTarget } from 'react';
import { MSProductVariantWithAttribute } from './query/product-query';

export const redirectedPathName = (locale: string, pathName: string) => {
    if (!pathName) return '/';

    const pathnameIsMissingLocale = languages.every(
        (locale) =>
            !pathName.startsWith(`/${locale}/`) && pathName !== `/${locale}`,
    );

    if (pathnameIsMissingLocale) {
        if (locale === fallbackLng) return pathName;
        return `/${locale}${pathName}`;
    } else {
        if (locale === fallbackLng) {
            const segments = pathName.split('/');
            const isHome = segments.length === 2;
            if (isHome) return '/';

            segments.splice(1, 1);
            return segments.join('/');
        }

        const segments = pathName.split('/');
        segments[1] = locale;
        return segments.join('/');
    }
};

// A preview URL (/{lang}/preview/page/{...slug}/{token} or
// /{lang}/preview/article/{...slug}/{token}) renders the same header/nav as
// the live page it previews, so active-nav matching must compare against the
// path being previewed, not the preview route itself — the slug segments
// mirror that live path exactly (see buildPreviewSlugPrefix in backoffice).
export const resolveActiveNavPathname = (pathname: string): string => {
    const match = pathname.match(/\/preview\/(?:page|article)\/(.+)$/);

    if (!match) {
        return pathname;
    }

    const [, tail] = match;
    const liveSegments = tail.split('/').filter(Boolean).slice(0, -1); // drop the trailing token

    return liveSegments.length ? `/${liveSegments.join('/')}` : '/';
};

export const getEasyFormUrl = (CustomerAccount: string) => {
    if (CustomerAccount) {
        const data = JSON.stringify({ customer_account: CustomerAccount });

        const key = CryptoJS.enc.Utf8.parse(
            `${process.env.NEXT_PUBLIC_EASYFORM_SECRET}`,
        );
        const iv = CryptoJS.enc.Utf8.parse(
            `${process.env.NEXT_PUBLIC_EASYFORM_IV}`,
        );

        const encrypted = CryptoJS.AES.encrypt(data, key, {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        }).toString();

        const token = encodeURIComponent(encrypted);

        if (token)
            return `${process.env.NEXT_PUBLIC_EASYFORM_URL}/api/oauth/token?code=${token}`;
    }

    return `${process.env.NEXT_PUBLIC_EASYFORM_URL}`;
};

export const NewObjectID = () => {
    const id = new ObjectID();
    return id.toString();
};

export const updateSearchParams = (type: string, value: string) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set(type, value);
    const newPathName = `${
        window.location.pathname
    }?${searchParams.toString()}`;
    return newPathName;
};

export const toPriceString = (price: number) => {
    const formatter = new Intl.NumberFormat('th', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    return padSingleDecimal(formatter.format(price));
};

export const toNumberString = (value: number): string => {
    const formatter = new Intl.NumberFormat('th', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    return formatter.format(value);
};

export const hideMobileNumberDigit = (phoneNumber: string) => {
    return (
        phoneNumber.slice(0, 4) +
        phoneNumber.slice(4, -2).replace(/./g, '*') +
        phoneNumber.slice(-2)
    );
};

export const isGuestUser = (email: string) => {
    return email.includes('@dummy.com');
};

/**
 *  Tranform Array To Dictionary
 * @param values Data Array
 * @param selector Node Unquie Key
 * @returns
 */
export const arrayToDict = <
    T extends { [K in keyof T]: string | number | symbol | any },
    K extends keyof T,
>(
    values: T[],
    selector: K,
): Record<string, T> => {
    return values.reduce(
        (pre, curr) => {
            return {
                ...pre,
                [curr[selector]]: curr,
            };
        },
        {} as Record<T[K], T>,
    );
};

export const DateToString = (date: Date | null, formatString: string) => {
    if (!date) return '';
    const formatter = format(date, formatString);
    return formatter;
};

export const DateText = (
    date: any,
    showtime = false,
    shortmonth = false,
    locale = 'th',
) => {
    if (date == null) return '';
    let dt = format(new Date(date), 'dd MM yyyy HH:mm:ss');
    let strDate = dt.split(' ');

    const month_thai = [
        'มกราคม',
        'กุมภาพันธ์',
        'มีนาคม',
        'เมษายน',
        'พฤษภาคม',
        'มิถุนายน',
        'กรกฎาคม',
        'สิงหาคม',
        'กันยายน',
        'ตุลาคม',
        'พฤศจิกายน',
        'ธันวาคม',
    ];

    const month_english = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    const month_short_english = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ];

    const month_short_thai = [
        'ม.ค.',
        'ก.พ.',
        'มี.ค.',
        'เม.ย.',
        'พ.ค.',
        'มิ.ย.',
        'ก.ค.',
        'ส.ค.',
        'ก.ย.',
        'ต.ค.',
        'พ.ย.',
        'ธ.ค.',
    ];

    const month_chinese = [
        '一月',
        '二月',
        '三月',
        '四月',
        '五月',
        '六月',
        '七月',
        '八月',
        '九月',
        '十月',
        '十一月',
        '十二月',
    ];

    let mont_use = [];

    if (locale == 'th') {
        if (shortmonth) {
            mont_use = month_short_thai;
        } else {
            mont_use = month_thai;
        }
    } else if (locale == 'zh') {
        // Chinese month names have no distinct short form; reuse the full name either way.
        mont_use = month_chinese;
    } else {
        if (shortmonth) {
            mont_use = month_short_english;
        } else {
            mont_use = month_english;
        }
    }
    const dateno = parseInt(strDate[1]);
    const years = parseInt(strDate[2]);
    const budhayearsAdd = 543;
    let strResult =
        strDate[0] +
        ' ' +
        mont_use[dateno - 1] +
        ' ' +
        (years + budhayearsAdd) +
        ' ' +
        (showtime ? strDate[3] : '');

    return strResult;
};

export const setMarkXBankAccount = (account: string) => {
    if (!account) return '';
    var markX = account
        .split('')
        .map((char, index) => {
            return index < 6 ? '*' : char;
        })
        .join('');
    markX = [markX.slice(0, 3), '-', markX.slice(3)].join('');
    markX = [markX.slice(0, 5), '-', markX.slice(5)].join('');
    markX = [markX.slice(0, 11), '-', markX.slice(11)].join('');

    return markX;
};

export const setMarkXIdCard = (idCard: string) => {
    if (!idCard) return '';
    var markX = idCard
        .split('')
        .map((char, index) => {
            return index < 6 ? '*' : char;
        })
        .join('');
    markX = [markX.slice(0, 1), '-', markX.slice(1)].join('');
    markX = [markX.slice(0, 6), '-', markX.slice(6)].join('');
    markX = [markX.slice(0, 12), '-', markX.slice(12)].join('');
    markX = [markX.slice(0, 15), '-', markX.slice(15)].join('');

    return markX;
};

export const filterVariantOfPromotion = (
    productVariant: MSProductVariantWithAttribute[],
    promotions: any,
) => {
    return productVariant.filter((variant) => {
        if (promotions.length > 0) {
            const date = new Date();
            const isDuringPromotion =
                date >= promotions[0].StartAt && date <= promotions[0].EndAt;
            return isDuringPromotion
                ? variant.show_during_promotion
                : variant.show_after_promotion;
        }
        return true;
    });
};

export const getLinkTarget = (
    linkTarget: LinkTarget,
): HTMLAttributeAnchorTarget => {
    let target = '_self';
    if (linkTarget === LinkTarget.BLANK) {
        target = '_blank';
    } else if (linkTarget === LinkTarget.PARENT) {
        target = '_parent';
    } else if (linkTarget === LinkTarget.TOP) {
        target = '_top';
    }
    return target;
};

export function shuffle<T>(array: T[]): T[] {
    let currentIndex = array.length,
        randomIndex;

    // While there remain elements to shuffle.
    while (currentIndex != 0) {
        // Pick a remaining element.
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;

        // And swap it with the current element.
        [array[currentIndex], array[randomIndex]] = [
            array[randomIndex],
            array[currentIndex],
        ];
    }

    return array;
}

export const verifyThaiId = (thaiId: string): boolean => {
    const m = thaiId.match(/(\d{12})(\d)/);
    if (!m) {
        console.warn('Bad input from user, invalid thaiId=', thaiId);
        return false;
    }
    const digits = m[1].split('');
    const sum = digits.reduce((total: number, digit: string, i: number) => {
        return total + (13 - i) * +digit;
    }, 0);
    const lastDigit = `${(11 - (sum % 11)) % 10}`;
    const inputLastDigit = m[2];
    if (lastDigit !== inputLastDigit) {
        console.warn('Bad input from user, invalid checksum thaiId=', thaiId);
        return false;
    }
    return true;
};

export const truncateString = (str: string, n: number): string => {
    return str.length > n ? str.slice(0, n - 1) + ' ...' : str;
};

export const copyToClipboard = async (str: string): Promise<boolean> => {
    try {
        await navigator.clipboard.writeText(str);
        return true;
    } catch (err: any) {
        throw new Error(err.message);
    }
};

export const copyToIOSClipboard = async (str: string): Promise<boolean> => {
    try {
        const clipboardItem = new ClipboardItem({
            'text/plain': new Promise(async (resolve) => {
                resolve(new Blob([str], { type: 'text/plain' }));
            }),
        });
        await navigator.clipboard.write([clipboardItem]);
        return true;
    } catch (err: any) {
        throw new Error(err.message);
    }
};

export const getBaseUrl = (url: string) => {
    return url.substring(0, url.lastIndexOf('.'));
};

export const encodeDynamicParts = (url: string): string => {
    const regexCollection = /^\/collections\/(.+)$/;
    const matchCollection = url.match(regexCollection);

    if (matchCollection) {
        const encodedUrl = `/collections/${encodeURIComponent(
            matchCollection[1],
        )}`;
        return encodedUrl;
    }

    const regexCollectionProduct =
        /^\/collections\/([^/]+)\/products\/([^/]+)$/;
    const matchCollectionProduct = url.match(regexCollectionProduct);

    if (matchCollectionProduct) {
        const encodedUrl = `/collections/${encodeURIComponent(
            matchCollectionProduct[1],
        )}/products/${encodeURIComponent(matchCollectionProduct[2])}`;
        return encodedUrl;
    }

    const regexProduct = /^\/products\/(.+)$/;
    const matchProduct = url.match(regexProduct);

    if (matchProduct) {
        const encodedUrl = `/products/${encodeURIComponent(matchProduct[1])}`;
        return encodedUrl;
    }

    return url;
};

export function isEncoded(url: string) {
    try {
        const pathname = new URL(url, 'http://localhost').pathname;
        const parts = pathname.split('/').filter(Boolean);
        return parts.every(
            (part) => part === encodeURIComponent(decodeURIComponent(part)),
        );
    } catch {
        return false;
    }
}

const slugify = (s: string | number) =>
    String(s).trim().toLowerCase().replace(/\s+/g, '-');

type Combo = {
    slug: string;
    pairs: { title: string; value: string }[];
};

function buildVariantCombos(variantPresets: MSProductVariantPreset[]): Combo[] {
    // Prepare per-attribute tokens with originals
    const perAttrOptions = variantPresets.map((preset) => {
        const attbSlug = slugify(preset.attb);
        return preset.attbValue.map((value) => {
            const valueSlug = slugify(value);
            return {
                token: `${attbSlug}-${valueSlug}`,
                pair: { title: preset.attb, value },
            };
        });
    });

    // Doing depth-first search (DFS) to build all combinations
    const results: Combo[] = [];
    const dfs = (
        idx: number,
        curTokens: string[],
        curPairs: { title: string; value: string }[],
    ) => {
        if (idx === perAttrOptions.length) {
            results.push({ slug: curTokens.join('-'), pairs: curPairs });
            return;
        }
        for (const opt of perAttrOptions[idx]) {
            dfs(idx + 1, [...curTokens, opt.token], [...curPairs, opt.pair]);
        }
    };

    if (perAttrOptions.length === 0) return [];
    dfs(0, [], []);
    return results;
}

export const mapPairSkuToVariantPresets = (
    variantPresets: MSProductVariantPreset[],
    pairSku: string,
) => {
    const output: { title: string; value: string }[] = [];
    const skuSlug = slugify(pairSku);
    const haystack = `-${skuSlug}-`;

    // Build all full combinations
    const combos = buildVariantCombos(variantPresets);

    // Prefer the most specific (more segments / longer) just in case
    combos.sort((a, b) => {
        const segDiff = b.slug.split('-').length - a.slug.split('-').length;
        return segDiff !== 0 ? segDiff : b.slug.length - a.slug.length;
    });

    // Match the combo at the END of the pairSku only.
    for (const combo of combos) {
        const needle = `-${combo.slug}-`;
        if (haystack.endsWith(needle)) {
            // Return exactly the pairs that formed this combo
            output.push(...combo.pairs);
            break;
        }
    }

    return output;
};

export function formatCurrencyFormat(
    price: string,
    format: string,
    priceSize: 'normal' | 'large' = 'normal',
    priceBold: boolean = false,
) {
    let priceMarkup = price;

    if (priceSize === 'large') {
        priceMarkup = `<span class="text-3xl">${price}</span>`;
    }

    if (priceBold) {
        priceMarkup = `<span class="font-bold">${priceMarkup}</span>`;
    }

    return format.replace('{price}', priceMarkup);
}

export function escapeRegExpChars(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export const padSingleDecimal = (val: string): string => {
    if (!val) return val;
    // Remove grouping separators (e.g. 1,234.5) to inspect decimals, then re-use original formatting minus grouping changes.
    // Because toPriceString already localized (Thai) it might use comma as thousands.
    // We'll parse numerically and then reconstruct only the decimal padding part safely.
    // Simple approach: detect pattern of ...\.[0-9]$ (single digit at end before optional whitespace / currency placeholder)
    // We only operate if the value ends with exactly one decimal digit and no following non-digit.
    const singleDecimalMatch = /^(.*\d)\.([0-9])(\s*)$/.exec(val);
    if (singleDecimalMatch) {
        return `${singleDecimalMatch[1]}.${singleDecimalMatch[2]}0${singleDecimalMatch[3]}`;
    }
    return val;
};

export const getProductPriceByLevel = (variant: any, type: CUSTOMER_LEVEL) => {
    const trialPrice = variant.priceTrial ?? variant.priceFull;
    const fullPrice = variant.priceFull;
    const trialComparePrice =
        variant.comparePriceTrial ?? variant.comparePriceFull;
    const fullComparePrice = variant.comparePriceFull;

    const getValue = () => {
        switch (type) {
            case CUSTOMER_LEVEL.BP:
                return trialPrice;
            case CUSTOMER_LEVEL.GUEST:
                return fullPrice;
            default:
                return fullPrice;
        }
    };

    const getSpecialValue = () => {
        switch (type) {
            case CUSTOMER_LEVEL.BP:
                return trialComparePrice != null ? trialComparePrice : '';
            case CUSTOMER_LEVEL.GUEST:
                return fullComparePrice != null ? fullComparePrice : '';
            default:
                return '';
        }
    };

    const isHaveSpecialPrice = () => {
        switch (type) {
            case CUSTOMER_LEVEL.BP:
                return trialComparePrice != null
                    ? trialComparePrice < trialPrice
                    : false;
            case CUSTOMER_LEVEL.GUEST:
                return fullComparePrice != null
                    ? fullComparePrice < fullPrice
                    : false;
            default:
                return false;
        }
    };

    return isHaveSpecialPrice() ? getSpecialValue() : getValue();
};
export type CardNumberFormatResult = {
    raw: string;
    formatted: string;
};

export function formatCardNumberInput(
    input: string,
    options?: {
        maxDigits?: number;
        groupSize?: number;
        separator?: string;
    },
): CardNumberFormatResult {
    const maxDigits = options?.maxDigits ?? 16;
    const groupSize = options?.groupSize ?? 4;
    const separator = options?.separator ?? '-';

    const raw = (input ?? '').replace(/\D/g, '').slice(0, maxDigits);
    const groups = raw.match(new RegExp(`.{1,${groupSize}}`, 'g'));
    const formatted = groups?.join(separator) || '';

    return { raw, formatted };
}

export type ExpiryDateFormatResult = {
    raw: string;
    formatted: string;
};

export function formatExpiryDateInput(input: string): ExpiryDateFormatResult {
    const raw = (input ?? '').replace(/\D/g, '').slice(0, 4);
    let formatted = raw;

    if (raw.length >= 2) {
        formatted = raw.slice(0, 2) + '/' + raw.slice(2);
    }

    if (formatted.length > 5) {
        formatted = formatted.slice(0, 5);
    }

    return { raw, formatted };
}

export function normalizeCVNInput(input: string, maxDigits = 4): string {
    return (input ?? '').replace(/\D/g, '').slice(0, maxDigits);
}

export const BUSINESS_EXPIRY_GRACE_DAYS = 7;

/**
 * A business stays accessible for `graceDays` calendar days, counting the
 * expiry day itself as day 1. With a 7 day grace period, an expiry of
 * 2026-07-28 keeps the storefront live through 2026-08-03 and blocks it from
 * 2026-08-04 00:00 (server time).
 *
 * Call this on the server only, so the verdict comes from the server clock
 * instead of the visitor's device clock.
 */
export function isBusinessExpired(
    expiredAt: string | Date | null | undefined,
    graceDays: number = BUSINESS_EXPIRY_GRACE_DAYS,
): boolean {
    if (!expiredAt) {
        return false;
    }

    const graceEnd = new Date(expiredAt);

    // An unparsable date keeps the storefront live rather than
    // blanking it out on bad data. Remove if expiry must fail closed.
    if (Number.isNaN(graceEnd.getTime())) {
        return false;
    }

    // Start of the expiry day, then jump graceDays forward: that midnight is
    // the first moment the storefront is closed.
    graceEnd.setHours(0, 0, 0, 0);
    graceEnd.setDate(graceEnd.getDate() + graceDays);

    return graceEnd <= new Date();
}
